// Implements: F-IDN-012, F-UIX-009, F-UIX-007
/**
 * P2 `/restablecer/:token` (v3 `vReset`): the new password, its orientative strength meter, and
 * its confirmation. Pure presentation; both fields stay uncontrolled (a secret never reaches the
 * markup's `value` attribute) and report through their handlers.
 *
 * `newError` is said under the new password (a missing one, or the server's policy refusal, said at the
 * field as the account page does: at the field, tied by `aria-describedby` together with the meter's help); `confirmError`
 * under the confirmation (they do not match). The server's failure sits above (`role="alert"`).
 * The v3 subtitle «Para <correo>» is omitted: no route reads a token's address (backend gap).
 */

import type { ReactElement, RefObject } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, errorId } from "../../../shared/ui/molecules/Field";
import { PasswordStrength } from "../../../shared/ui/molecules/PasswordStrength";
import login from "./LoginForm.module.css";
import { RecoveryBrand } from "./RecoveryBrand";

export const RESET_NEW_FIELD_ID = "reset-new";
export const RESET_CONFIRM_FIELD_ID = "reset-confirm";
const STRENGTH_HELP_ID = "reset-new-strength";

export interface ResetPasswordFormProps {
  readonly password: string;
  readonly onPasswordChange: (value: string) => void;
  readonly onConfirmChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly submitting: boolean;
  readonly newError: string | undefined;
  readonly confirmError: string | undefined;
  readonly formError: string | undefined;
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
  readonly newRef: RefObject<HTMLInputElement | null>;
  readonly confirmRef: RefObject<HTMLInputElement | null>;
}

export function ResetPasswordForm({
  password,
  onPasswordChange,
  onConfirmChange,
  onSubmit,
  submitting,
  newError,
  confirmError,
  formError,
  headingRef,
  newRef,
  confirmRef,
}: ResetPasswordFormProps): ReactElement {
  const { t } = useTranslation();
  return (
    <form
      className={login.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <RecoveryBrand />
      <div>
        <h1 className={login.title} tabIndex={-1} ref={headingRef}>
          {t("auth.reset.title")}
        </h1>
      </div>
      {formError === undefined ? null : (
        <p className={login.error} role="alert">
          <Icon name="alert-circle" size="sm" />
          <span>{formError}</span>
        </p>
      )}
      <Field id={RESET_NEW_FIELD_ID} label={t("auth.reset.new")} error={newError}>
        <Input
          id={RESET_NEW_FIELD_ID}
          type="password"
          onChange={onPasswordChange}
          autoComplete="new-password"
          invalid={newError !== undefined}
          describedBy={newError === undefined ? STRENGTH_HELP_ID : `${STRENGTH_HELP_ID} ${errorId(RESET_NEW_FIELD_ID)}`}
          inputRef={newRef}
        />
        <PasswordStrength id={STRENGTH_HELP_ID} password={password} />
      </Field>
      <Field id={RESET_CONFIRM_FIELD_ID} label={t("auth.reset.confirm")} error={confirmError}>
        <Input
          id={RESET_CONFIRM_FIELD_ID}
          type="password"
          onChange={onConfirmChange}
          autoComplete="new-password"
          invalid={confirmError !== undefined}
          describedBy={confirmError === undefined ? undefined : errorId(RESET_CONFIRM_FIELD_ID)}
          inputRef={confirmRef}
        />
      </Field>
      <Button variant="primary" type="submit" loading={submitting}>
        {t("auth.reset.submit")}
      </Button>
    </form>
  );
}
